/**
 * User Segmentation Analytics Utilities
 *
 * Calculates user segmentation statistics based on financial app experience data
 * collected during the welcome flow.
 */

/**
 * Calculate user segmentation statistics from users data
 * @param {Object} usersData - Firebase users object
 * @returns {Object} Segmentation statistics
 */
export const calculateUserSegmentation = (usersData) => {
  if (!usersData) return getEmptySegmentation();

  const stats = {
    total: 0,
    totalAllUsers: 0,
    byExperience: {
      new: 0,
      occasional: 0,
      'power-user': 0
    },
    byApp: {},
    byDiscovery: {
      tiktok: 0,
      'app-store': 0,
      other: 0
    },
    newUsersLast7Days: 0,
    newUsersLast30Days: 0,
    usersWithoutData: 0
  };

  const now = new Date();
  const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

  Object.entries(usersData).forEach(([userId, userData]) => {
    stats.totalAllUsers++;

    // Only include users who have completed the welcome flow (have financeAppExperience data)
    if (!userData.financeAppExperience) {
      stats.usersWithoutData++;
      return; // Skip this user - they haven't completed the new welcome flow
    }

    // This user has segmentation data, count them
    stats.total++;

    // Experience level segmentation
    const experience = userData.financeAppExperience;
    stats.byExperience[experience] = (stats.byExperience[experience] || 0) + 1;

    // Apps used aggregation
    if (userData.financeAppsUsed && Array.isArray(userData.financeAppsUsed)) {
      userData.financeAppsUsed.forEach(app => {
        if (app !== 'none') {
          stats.byApp[app] = (stats.byApp[app] || 0) + 1;
        }
      });
    }

    // Discovery source
    if (userData.discoverySource) {
      const source = userData.discoverySource;
      stats.byDiscovery[source] = (stats.byDiscovery[source] || 0) + 1;
    }

    // Time-based segmentation
    const creationDate = userData.accountCreationDate
      ? new Date(userData.accountCreationDate)
      : null;

    if (creationDate) {
      if (creationDate >= sevenDaysAgo) stats.newUsersLast7Days++;
      if (creationDate >= thirtyDaysAgo) stats.newUsersLast30Days++;
    }
  });

  return stats;
};

/**
 * Calculate percentage distribution for experience levels
 * @param {Object} stats - Segmentation statistics
 * @returns {Object} Percentage distribution
 */
export const getExperienceDistribution = (stats) => {
  if (!stats || stats.total === 0) return {
    new: 0,
    occasional: 0,
    powerUser: 0
  };

  return {
    new: Math.round((stats.byExperience.new / stats.total) * 100),
    occasional: Math.round((stats.byExperience.occasional / stats.total) * 100),
    powerUser: Math.round((stats.byExperience['power-user'] / stats.total) * 100)
  };
};

/**
 * Get top N apps by usage
 * @param {Object} stats - Segmentation statistics
 * @param {number} limit - Maximum number of apps to return
 * @returns {Array} Top apps with counts and percentages
 */
export const getTopApps = (stats, limit = 5) => {
  if (!stats || !stats.byApp) return [];

  return Object.entries(stats.byApp)
    .filter(([app, _]) => app !== 'none')
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([app, count]) => ({
      app,
      count,
      percentage: Math.round((count / stats.total) * 100)
    }));
};

/**
 * Get empty segmentation object
 * @returns {Object} Empty segmentation statistics
 */
const getEmptySegmentation = () => ({
  total: 0,
  totalAllUsers: 0,
  byExperience: {
    new: 0,
    occasional: 0,
    'power-user': 0
  },
  byApp: {},
  byDiscovery: {
    tiktok: 0,
    'app-store': 0,
    other: 0
  },
  newUsersLast7Days: 0,
  newUsersLast30Days: 0,
  usersWithoutData: 0
});

/**
 * Human-readable app labels
 */
export const APP_LABELS = {
  ynab: 'YNAB',
  mint: 'Mint',
  everydollar: 'EveryDollar',
  copilot: 'Copilot',
  monarch: 'Monarch',
  pocketguard: 'PocketGuard',
  spreadsheets: 'Spreadsheets',
  'pen-paper': 'Pen & Paper',
  'bank-app': 'Bank App',
  none: 'None'
};

/**
 * Human-readable experience level labels
 */
export const EXPERIENCE_LABELS = {
  new: 'New to Finance Apps',
  occasional: 'Occasional Users',
  'power-user': 'Power Users',
  unknown: 'Not Specified'
};
